import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"

export interface MultiSelectOption {
  label: string
  value: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  maxDisplay?: number // how many selected to show in trigger before summarizing
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  disabled = false,
  className = "",
  maxDisplay = 2,
}) => {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  // Filter options by search
  const filteredOptions = React.useMemo(() => {
    if (!search) return options
    return options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()))
  }, [options, search])

  // Display selected labels in trigger
  // const selectedLabels = options.filter(opt => selected.includes(opt.value)).map(opt => opt.label)
  // let triggerText = placeholder
  // if (selectedLabels.length === 1) triggerText = selectedLabels[0]
  // else if (selectedLabels.length > 1) {
  //   if (selectedLabels.length <= maxDisplay) triggerText = selectedLabels.join(", ")
  //   else triggerText = `${selectedLabels.slice(0, maxDisplay).join(", ")}, +${selectedLabels.length - maxDisplay} more`
  // }
  const triggerText = placeholder;

  const handleSelect = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            `w-full justify-between border-0 font-sans text-base transition-colors
              ${selected.length > 0 ? 'bg-primary-800 !text-accent-50 !text-white' : 'bg-primary-500 !text-white'}
              data-[state=open]:text-accent-300 [&_svg]:!text-white
              hover:bg-primary-700 hover:!text-white focus:bg-primary-700 focus:!text-white`,
            className,
            disabled && "opacity-60 cursor-not-allowed"
          )}
        >
          <span className="truncate text-left">{triggerText}</span>
          <span className="flex items-center gap-2">
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-primary-50 rounded-lg shadow-lg border border-neutral-200 font-sans text-neutral-900 z-50">
        <Command>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
            className="h-9 px-3 font-sans text-neutral-900 mb-1 border-0 rounded-none bg-transparent focus:border-0 focus:outline-none focus:ring-0 shadow-none"
            disabled={disabled}
          />
          <CommandEmpty>No options found.</CommandEmpty>
          <CommandGroup>
            {filteredOptions.map((opt) => (
              <CommandItem
                key={opt.value}
                value={opt.value}
                onSelect={() => handleSelect(opt.value)}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:!bg-neutral-200 transition-colors data-[selected=true]:!bg-neutral-200"
              >
                <Checkbox
                  checked={selected.includes(opt.value)}
                  className="flex-shrink-0"
                />
                <span className="flex-1">{opt.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default MultiSelect 